import { useMemo, useState } from 'react';
import {
  Checkbox,
  Alert,
  Button,
  Card,
  Group,
  MultiSelect,
  NumberInput,
  SegmentedControl,
  Select,
  Stack,
  TextInput,
  Textarea,
  Title,
} from '@mantine/core';
import { Link, useNavigate, useSearchParams } from 'react-router';
import {
  SAHA_TURU_TIP_ETIKETI,
  TATBIKAT_TURLERI,
  TATBIKAT_TURU_COKLU_ETIKETI,
  TATBIKAT_TURU_ETIKETI,
  sahaTuruTipiSchema,
  sahaTuruAlaniVarMi,
} from '@isg/validation';
import type { SahaTuruTipi, TatbikatTuru } from '@isg/validation';
import { useSahaTuruAc, useSahaTuruSecenekleri } from '../api/queries';
import { QueryStatus } from './parts';
import { ANLAM } from '../theme';
import { gunGirdisiIso } from '../lib/format';
import { useVarsayilanGun } from '../lib/saha-gunu';

/**
 * SAHA TURU AÇMA — AYRI SAYFA (P1: tur bağımsız kayıttır; Kapsam §27/S1 · S8).
 *
 * Künye: tip · işyeri (+ isteğe bağlı lokasyon) · denetlenen birim (isteğe
 * bağlı — liste boşken tur açılmaz DEĞİL) · tarih · denetimde ŞABLON. Ekip
 * turun sayfasında satır içi eklenir (P1-a); lider ekipte otomatiktir.
 *
 * ⚠️ Şablon listesi işyerinin şirketine göre süzülür: bahçeli (`yerel`) bir
 * şablon başka şirketin turunda görünmez — uç zaten reddederdi, ekran o
 * yolu hiç açmaz (§2.40).
 */
export function SahaTuruYeniScreen(): React.JSX.Element {
  const navigate = useNavigate();
  const secenekler = useSahaTuruSecenekleri();
  const ac = useSahaTuruAc();
  // Acil durum sayfasından *"Tatbikat aç"* (Kapsam §29/E5): tip ve işyeri
  // URL'den ön dolar — seçtirmez, seçili gelir; kullanıcı değiştirebilir.
  const [arama] = useSearchParams();
  const onTip = sahaTuruTipiSchema.safeParse(arama.get('tip'));
  const [tip, setTip] = useState<SahaTuruTipi>(onTip.success ? onTip.data : 'gozlem');
  const [workplaceId, setWorkplaceId] = useState<string | null>(arama.get('workplaceId'));
  // E5 — tatbikat alanları (yalnız tip = tatbikat'ta gönderilir).
  // G4.1 — tür ÇOKLU (olay sınıfı deseni: P10 kutu ızgarası, açılır listeye gömülmez).
  const [tatbikatTurleri, setTatbikatTurleri] = useState<TatbikatTuru[]>(['yangin']);
  const [senaryo, setSenaryo] = useState('');
  const [sureDakika, setSureDakika] = useState<number | string>('');
  const [tahliye, setTahliye] = useState<number | string>('');
  const [ekipler, setEkipler] = useState<string[]>([]);
  const [locationNodeId, setLocationNodeId] = useState<string | null>(null);
  const [birimRefId, setBirimRefId] = useState<string | null>(null);
  const [formTemplateId, setFormTemplateId] = useState<string | null>(null);
  // Madde 302: varsayılan "bugün" SAHA GÜNÜ (sunucudan) — kullanıcı değiştirir.
  const [tarih, setTarih] = useVarsayilanGun();

  const isyerleri = secenekler.data?.isyerleri ?? [];
  const isyeri = isyerleri.find((w) => w.id === workplaceId) ?? null;
  const isyeriSecenekleri = useMemo(
    () => isyerleri.map((w) => ({ value: w.id, label: `${w.companyAd} — ${w.ad}` })),
    [isyerleri.map((w) => w.id).join(',')],
  );
  // ✏️ F4·S6 (T5): İSTEMCİ SÜZGECİ DÜŞTÜ. Burada eskiden bir KURAL vardı
  // (`companyId === null || companyId === isyeri.companyId` — "merkezî her
  // yerde geçerli") ve o kural işyeri kapsamlı şablonu ifade edemiyordu.
  // Artık uç şablonun KAPSAMINI taşır; ekran yalnız ÜYELİK bakar.
  const sablonlar = (secenekler.data?.sablonlar ?? []).filter(
    (s) => workplaceId !== null && s.kapsamIsyeriIdleri.includes(workplaceId),
  );

  const engel: string | null =
    workplaceId === null
      ? 'Önce işyerini seçin'
      : gunGirdisiIso(tarih) === null
        ? 'Geçerli bir tarih girin'
        : sahaTuruAlaniVarMi(tip, 'sablon') && formTemplateId === null
          ? 'Denetim için form şablonu seçin'
          : sahaTuruAlaniVarMi(tip, 'tatbikat') && tatbikatTurleri.length === 0
            ? 'Tatbikat için en az bir tür seçin'
            : sahaTuruAlaniVarMi(tip, 'tatbikat') && senaryo.trim() === ''
              ? 'Tatbikat için senaryo yazın'
              : null;

  return (
    <Stack data-testid="saha-turu-yeni-sayfasi">
      <Link to="/saha-turlari" data-testid="detay-geri">
        ← Saha turları
      </Link>
      <Title order={3}>Saha turu aç</Title>
      <QueryStatus queries={[secenekler]} />
      {ac.error && (
        <Alert color={ANLAM.engel} role="alert" data-testid="saha-turu-yeni-hata">
          {ac.error.message}
        </Alert>
      )}
      <Card withBorder>
        <Stack>
          <SegmentedControl
            data-testid="saha-turu-tip"
            value={tip}
            onChange={(v) => {
              const yeniTip = v as SahaTuruTipi;
              setTip(yeniTip);
              // KA-6: tipe ait olmayan seçim BIRAKILIR (envanter tek kaynak).
              if (!sahaTuruAlaniVarMi(yeniTip, 'sablon')) setFormTemplateId(null);
              if (!sahaTuruAlaniVarMi(yeniTip, 'birim')) setBirimRefId(null);
            }}
            data={Object.entries(SAHA_TURU_TIP_ETIKETI).map(([value, label]) => ({ value, label }))}
          />
          <Group align="flex-end" wrap="wrap">
            <Select
              w={320}
              label="İşyeri"
              data-testid="saha-turu-isyeri"
              searchable
              data={isyeriSecenekleri}
              value={workplaceId}
              onChange={(v) => {
                setWorkplaceId(v);
                setLocationNodeId(null);
                setFormTemplateId(null);
              }}
            />
            <Select
              w={240}
              label="Lokasyon"
              data-testid="saha-turu-lokasyon"
              placeholder={workplaceId ? 'İsteğe bağlı' : 'Önce işyeri'}
              clearable
              disabled={!workplaceId}
              data={(isyeri?.lokasyonlar ?? []).map((l) => ({ value: l.id, label: l.ad }))}
              value={locationNodeId}
              onChange={setLocationNodeId}
            />
            {sahaTuruAlaniVarMi(tip, 'birim') && (
              <Select
                w={240}
                label="Denetlenen birim"
                data-testid="saha-turu-birim"
                placeholder="İsteğe bağlı"
                clearable
                data={(secenekler.data?.birimler ?? []).map((b) => ({ value: b.id, label: b.ad }))}
                value={birimRefId}
                onChange={setBirimRefId}
              />
            )}
            <TextInput
              w={180}
              type="date"
              label="Tarih"
              data-testid="saha-turu-tarih"
              value={tarih}
              onChange={(e) => setTarih(e.currentTarget.value)}
            />
          </Group>
          {sahaTuruAlaniVarMi(tip, 'tatbikat') && (
            <Stack gap="xs" data-testid="saha-turu-tatbikat-alanlari">
              <Group align="flex-end" wrap="wrap">
                <Checkbox.Group
                  label={TATBIKAT_TURU_COKLU_ETIKETI}
                  value={tatbikatTurleri}
                  onChange={(v: string[]) => setTatbikatTurleri(v as TatbikatTuru[])}
                >
                  <Group gap="md" mt={6} data-testid="saha-turu-tatbikat-turu">
                    {TATBIKAT_TURLERI.map((tur) => (
                      <Checkbox
                        key={tur}
                        value={tur}
                        label={TATBIKAT_TURU_ETIKETI[tur]}
                        data-testid={`saha-turu-tatbikat-turu-${tur}`}
                      />
                    ))}
                  </Group>
                </Checkbox.Group>
                <NumberInput
                  w={160}
                  label="Süre (dk)"
                  data-testid="saha-turu-tatbikat-sure"
                  min={1}
                  value={sureDakika}
                  onChange={setSureDakika}
                />
                <NumberInput
                  w={200}
                  label="Tahliye edilen kişi"
                  data-testid="saha-turu-tatbikat-tahliye"
                  min={0}
                  value={tahliye}
                  onChange={setTahliye}
                />
                <MultiSelect
                  w={420}
                  label="Katılan ekipler"
                  data-testid="saha-turu-tatbikat-ekipler"
                  comboboxProps={{ withinPortal: true }}
                  value={ekipler}
                  onChange={setEkipler}
                  data={(secenekler.data?.acilDurumEkipleri ?? []).map((e) => ({
                    value: e.id,
                    label: e.ad,
                  }))}
                />
              </Group>
              <Textarea
                label="Senaryo"
                data-testid="saha-turu-tatbikat-senaryo"
                autosize
                minRows={2}
                value={senaryo}
                onChange={(e) => setSenaryo(e.currentTarget.value)}
              />
            </Stack>
          )}
          {sahaTuruAlaniVarMi(tip, 'sablon') && (
            <Select
              w={480}
              label="Form şablonu"
              data-testid="saha-turu-sablon"
              placeholder={workplaceId ? 'Şablon seçin' : 'Önce işyeri'}
              searchable
              disabled={!workplaceId}
              data={sablonlar.map((s) => ({ value: s.id, label: s.ad }))}
              value={formTemplateId}
              onChange={setFormTemplateId}
            />
          )}
          <Group justify="flex-end" align="center">
            {engel !== null && (
              <span style={{ fontSize: 12, opacity: 0.7 }} data-testid="saha-turu-kaydet-engeli">
                {engel}
              </span>
            )}
            <Button
              data-testid="saha-turu-kaydet"
              disabled={engel !== null || ac.isPending}
              loading={ac.isPending}
              onClick={() => {
                if (!workplaceId || gunGirdisiIso(tarih) === null) return;
                ac.mutate(
                  {
                    tip,
                    workplaceId,
                    locationNodeId,
                    birimRefId: sahaTuruAlaniVarMi(tip, 'birim') ? birimRefId : null,
                    formTemplateId: sahaTuruAlaniVarMi(tip, 'sablon') ? formTemplateId : null,
                    tarih,
                    ekipPersonIds: [],
                    tatbikat: sahaTuruAlaniVarMi(tip, 'tatbikat')
                      ? {
                          turler: tatbikatTurleri,
                          senaryo: senaryo.trim(),
                          sureDakika: sureDakika === '' ? null : Number(sureDakika),
                          tahliyeKisiSayisi: tahliye === '' ? null : Number(tahliye),
                          ekipGorevlendirmeTuruIds: ekipler,
                        }
                      : null,
                  },
                  { onSuccess: (tur) => void navigate(`/saha-turlari/${tur.id}`) },
                );
              }}
            >
              Turu aç
            </Button>
          </Group>
        </Stack>
      </Card>
    </Stack>
  );
}
